import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiRefreshCw, FiUser, FiClock, FiAlertCircle, FiShield } from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const Audit = () => {
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await gatewayApi.listAuditLogs(200);
      setLogs(response.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load audit logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return logs;
    return logs.filter(log =>
      [log.action, log.target, log.actor, log.status, log.details]
        .some(value => String(value || '').toLowerCase().includes(term))
    );
  }, [logs, searchTerm]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Audit Trail</h2>
          <p className="text-sm text-slate-500">Administrative and credential lifecycle events recorded in PocketBase.</p>
        </div>
        <button onClick={load} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 flex items-center gap-2 hover:bg-slate-50">
          <SafeIcon icon={FiRefreshCw} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2 px-4">
        <SafeIcon icon={FiSearch} className="text-slate-400" />
        <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search actions, targets or details..." className="w-full py-2 text-sm outline-none" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full text-left whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-100">
                <th className="px-5 py-4">Action</th>
                <th className="px-5 py-4">Target</th>
                <th className="px-5 py-4">Actor</th>
                <th className="px-5 py-4">Time</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr><td colSpan="6" className="px-5 py-12 text-center text-slate-400">Loading PocketBase audit trail...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-5 py-12 text-center text-slate-400">No audit events found.</td></tr>
              ) : filtered.map(log => (
                <tr key={log.id} className="hover:bg-slate-50/70">
                  <td className="px-5 py-4 font-bold text-slate-800">{log.action || '—'}</td>
                  <td className="px-5 py-4 text-slate-600">{log.target || '—'}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <SafeIcon icon={FiUser} className="text-slate-400" /> {log.actor || 'System'}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <SafeIcon icon={FiClock} /> {formatDate(log.logged_at || log.created)}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${String(log.status).toLowerCase() === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {log.status || 'Success'}
                    </span>
                  </td>
                  <td className="px-5 py-4 max-w-[340px] truncate text-xs text-slate-500">{log.details || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-slate-900 rounded-2xl p-5 text-white flex gap-3">
        <SafeIcon icon={FiShield} className="text-emerald-400 mt-0.5" />
        <div>
          <p className="text-sm font-bold">Administrative actions are server-side audited</p>
          <p className="text-xs text-slate-400 mt-1">Client edits, app changes, key generation, rotation, revocation and provider tests are recorded here.</p>
        </div>
      </div>
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-AU');
};

export default Audit;